'use client';
import { Cookies } from 'react-cookie';

const cookie = new Cookies();

const COOKIE_NAME = 'cr_tracking';

// URL param name → canonical camelCase key
const PARAM_MAP: Record<string, string> = {
  utm_source: 'utmSource',
  utm_campaign: 'utmCampaign',
  utm_medium: 'utmMedium',
  utm_term: 'utmTerm',
  utm_content: 'utmContent',
  utm_brand: 'utmBrand',
  gclid: 'gclid',
  msclkid: 'msclkid',
  fbclid: 'fbclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  c_id: 'cId',
  u_id: 'uId',
};

export type TrackingParams = Partial<Record<string, string>>;

function getRootDomain(): string {
  // e.g. "app.coverright.com" → ".coverright.com"
  const parts = document.location.host.replace(/:\d+$/, '').split('.');
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.');
  }
  return document.location.hostname;
}

export function captureTrackingParamsFromUrl(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const existing = getTrackingParams();
  const updated: TrackingParams = { ...existing };
  let changed = false;

  for (const [urlKey, canonicalKey] of Object.entries(PARAM_MAP)) {
    const value = urlParams.get(urlKey);
    if (value) {
      updated[canonicalKey] = value;
      changed = true;
    }
  }

  if (changed) {
    cookie.set(COOKIE_NAME, JSON.stringify(updated), {
      domain: getRootDomain(),
      path: '/',
      expires: new Date(2040, 1, 1),
    });
  }
}

export function getTrackingParams(): TrackingParams {
  try {
    const raw = cookie.get(COOKIE_NAME);
    if (!raw) return {};
    return typeof raw === 'string' ? JSON.parse(raw) : raw;
  } catch {
    return {};
  }
}

// Sets tracking params as fallback — only fills keys not already present in the cookie.
// Use for hardcoded per-page defaults; URL params captured earlier always win.
export function setTrackingParamsFallback(fallback: TrackingParams): void {
  const existing = getTrackingParams();
  const updated: TrackingParams = { ...existing };
  let changed = false;

  for (const [key, value] of Object.entries(fallback)) {
    if (value && !existing[key]) {
      updated[key] = value;
      changed = true;
    }
  }

  if (changed) {
    cookie.set(COOKIE_NAME, JSON.stringify(updated), {
      domain: getRootDomain(),
      path: '/',
      expires: new Date(2040, 1, 1),
    });
  }
}

export function getTrackingParamsAsAttributes(): Array<{ key: string; value: string }> {
  const params = getTrackingParams();
  return Object.entries(params)
    .filter(([, v]) => Boolean(v))
    .map(([key, value]) => ({ key, value: value as string }));
}
